import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { cartPerformance } from '@theme/performance';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/** How long the inline error stays on screen before it is cleared. */
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

/** How long the success announcement stays in the live region for screen readers. */
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A single variant of the cross-sell product, as serialised by the block.
 *
 * @typedef {object} CrossSellVariant
 * @property {number} id
 * @property {boolean} available
 * @property {number} price - Price in minor units (cents).
 * @property {string[]} options - Option values, ordered by option position.
 * @property {string} [image]
 * @property {string} [image2x]
 */

/**
 * @typedef {object} CrossSellPayload
 * @property {string[][]} options - Every option value of the cross-sell product, by option position.
 * @property {CrossSellVariant[]} variants
 */

/**
 * @typedef {object} BroCrossSellBundleRefs
 * @property {HTMLScriptElement} variantData - JSON payload holding the cross-sell options and variants.
 * @property {HTMLButtonElement} addButton - The "add both to cart" button.
 * @property {HTMLElement} addButtonText - The label inside the button.
 * @property {HTMLElement} bundleTotal - The discounted bundle total.
 * @property {HTMLElement} errorMessage - Inline error container.
 * @property {HTMLElement} liveRegion - Polite live region for status announcements.
 * @property {HTMLSelectElement[]} [optionSelects] - One select per cross-sell product option.
 * @property {HTMLElement} [savingsBadge] - "Save X%" pill in the header.
 * @property {HTMLElement} [itemPriceWas] - Struck-through price of the cross-sell item.
 * @property {HTMLElement} [itemPriceNow] - Effective price of the cross-sell item.
 * @property {HTMLElement} [regularTotal] - Struck-through bundle total before the discount.
 * @property {HTMLElement} [savingsLine] - "You save X" line.
 * @property {HTMLImageElement} [mainImage] - Thumbnail of the product being viewed.
 * @property {HTMLImageElement} [crossImage] - Thumbnail of the cross-sell product.
 *
 * @extends {Component<BroCrossSellBundleRefs>}
 */
class BroCrossSellBundle extends Component {
  requiredRefs = ['variantData', 'addButton', 'addButtonText', 'bundleTotal', 'errorMessage', 'liveRegion'];

  /** @type {CrossSellVariant[]} */
  #variants = [];

  /** @type {string[][]} */
  #optionValues = [];

  /** @type {CrossSellVariant | null} */
  #selectedVariant = null;

  /** @type {Set<string> | null} */
  #kidsSizes = null;

  /**
   * Whether the shopper picked the companion's size themselves. Once they have, the product page's
   * size picker stops overwriting that choice and only ever narrows the range it can be chosen from.
   */
  #sizeChosenByShopper = false;

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #errorTimeout;

  /** @type {number | undefined} */
  #successTimeout;

  #isSubmitting = false;

  /** Money format with HTML entities resolved, so prices can be written via textContent. */
  #moneyFormat = '';

  connectedCallback() {
    super.connectedCallback();

    this.#moneyFormat = decodeEntities(this.dataset.moneyFormat ?? '');

    const payload = this.#parsePayload();
    this.#variants = payload.variants;
    this.#optionValues = payload.options;

    // The server already rendered the offer for the right variant and size range; this first pass
    // adopts that state and keeps the per-option "unavailable" hints in sync with it.
    this.#syncOptions();
    this.#render();

    const { signal } = this.#abortController;
    this.closest('.shopify-section, dialog')?.addEventListener(
      StandardEvents.productSelect,
      this.#handleMainProductSelect,
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    clearTimeout(this.#errorTimeout);
    clearTimeout(this.#successTimeout);
  }

  /* -------------------------------------------------------------------------------------------
   * Settings, read straight off the dataset so a section re-render can never leave them stale.
   * ---------------------------------------------------------------------------------------- */

  /** @returns {number} Price of the currently selected variant of the product being viewed. */
  get #mainPrice() {
    return Number(this.dataset.mainPrice) || 0;
  }

  /** @returns {boolean} */
  get #mainAvailable() {
    return this.dataset.mainAvailable !== 'false';
  }

  /** @returns {number} */
  get #discountPercentage() {
    return Math.min(Math.max(Number(this.dataset.discountPercentage) || 0, 0), 100);
  }

  /**
   * Fixed total for the pair in cents, or 0 when the percentage applies instead.
   *
   * Kids and adult ranges rarely cost the same, so a pair can price them separately. The kids
   * figure is an override: left unset, kids sizes use the pair's main price.
   * @returns {number}
   */
  get #fixedBundlePrice() {
    const kidsPrice = Math.max(Number(this.dataset.bundlePriceKids) || 0, 0);
    if (kidsPrice > 0 && this.#sizeGroup === 'kids') return kidsPrice;

    return Math.max(Number(this.dataset.bundlePrice) || 0, 0);
  }

  /** @returns {HTMLSelectElement[]} */
  get #selects() {
    const { optionSelects } = this.refs;
    if (!optionSelects) return [];

    const elements = Array.isArray(optionSelects) ? optionSelects : [optionSelects];

    return /** @type {HTMLSelectElement[]} */ (elements.filter((element) => element instanceof HTMLSelectElement));
  }

  /* -------------------------------------------------------------------------------------------
   * Size ranges
   *
   * Kids and adult sizes live in the same option, so a jersey in 140 must not be bundled with
   * shorts in XL. The block works out which option links the two products; everything here keeps
   * the companion's dropdown inside the range the shopper picked on the product page.
   * ---------------------------------------------------------------------------------------- */

  /** @returns {number} Index of the linked size option, or -1 when the products share none. */
  get #sizeOptionIndex() {
    const index = Number(this.dataset.sizeOptionIndex);

    return Number.isInteger(index) ? index : -1;
  }

  /** @returns {Set<string>} Normalised values that count as kids sizes. */
  get #kidsSizeSet() {
    this.#kidsSizes ??= new Set(
      (this.dataset.kidsSizes ?? '')
        .split('|')
        .map((value) => normalise(value))
        .filter(Boolean)
    );

    return this.#kidsSizes;
  }

  /**
   * The range the product page is currently in, or null when the products share no size option
   * and there is no range to speak of.
   * @returns {'kids' | 'adult' | null}
   */
  get #sizeGroup() {
    const hostSize = normalise(this.dataset.mainSizeValue ?? '');
    const kidsSizes = this.#kidsSizeSet;

    if (this.#sizeOptionIndex < 0 || !hostSize || !kidsSizes.size) return null;

    return kidsSizes.has(hostSize) ? 'kids' : 'adult';
  }

  /**
   * The companion values the shopper may choose from, or null when no narrowing applies - either
   * because the products share no size option, or because narrowing would empty the dropdown.
   * @returns {Set<string> | null}
   */
  #allowedSizeValues() {
    const sizeIndex = this.#sizeOptionIndex;
    const group = this.#sizeGroup;

    if (group == null) return null;

    const kidsSizes = this.#kidsSizeSet;
    const hostIsKids = group === 'kids';
    const allowed = (this.#optionValues[sizeIndex] ?? []).filter(
      (value) => kidsSizes.has(normalise(value)) === hostIsKids
    );

    // A companion with no counterpart in the shopper's range keeps its full list. An unusable
    // dropdown would be worse than one that offers the wrong range.
    return allowed.length ? new Set(allowed.map((value) => normalise(value))) : null;
  }

  /**
   * Picks the companion size to settle on: the shopper's own size when the product page offers it,
   * then whatever they already had, then the first size in range that can actually be bought.
   * @param {number} sizeIndex
   * @param {string[]} values - Values the select will offer, in the product's own option order.
   * @param {string} currentValue
   * @returns {string}
   */
  #pickSizeValue(sizeIndex, values, currentValue) {
    const hostSize = normalise(this.dataset.mainSizeValue ?? '');
    const isAvailable = (/** @type {string} */ value) => Boolean(this.#variantForSize(sizeIndex, value)?.available);

    const mirrored = values.find((value) => normalise(value) === hostSize);
    if (mirrored && isAvailable(mirrored)) return mirrored;

    if (values.includes(currentValue)) return currentValue;

    return values.find(isAvailable) ?? values[0] ?? currentValue;
  }

  /**
   * The variant the companion would resolve to if only its size changed.
   * @param {number} sizeIndex
   * @param {string} value
   * @returns {CrossSellVariant | undefined}
   */
  #variantForSize(sizeIndex, value) {
    const combination = this.#selects.map((select) => select.value);
    combination[sizeIndex] = value;

    return this.#findVariant(combination);
  }

  /* -------------------------------------------------------------------------------------------
   * Variant resolution
   * ---------------------------------------------------------------------------------------- */

  /** @returns {CrossSellPayload} */
  #parsePayload() {
    try {
      const parsed = JSON.parse(this.refs.variantData.textContent ?? '{}');

      return {
        options: Array.isArray(parsed?.options) ? parsed.options : [],
        variants: Array.isArray(parsed?.variants) ? parsed.variants : [],
      };
    } catch (error) {
      console.error('[bro-cross-sell-bundle] Could not parse variant data', error);
      return { options: [], variants: [] };
    }
  }

  /**
   * Finds the variant matching a full set of option values. The block renders one select per
   * entry of `options_with_values`, so select order and `variant.options` order are the same.
   * @param {string[]} optionValues
   * @returns {CrossSellVariant | undefined}
   */
  #findVariant(optionValues) {
    return this.#variants.find(
      (variant) =>
        variant.options.length === optionValues.length &&
        variant.options.every((value, index) => value === optionValues[index])
    );
  }

  /**
   * Brings the dropdowns and the selected variant back in step: narrows the size option to the
   * shopper's range, settles on a value for every option, and re-labels the values that cannot
   * currently be bought.
   *
   * Values are left selectable rather than disabled - a shopper who lands on an unavailable
   * combination sees a disabled "Sold out" button rather than a trap they cannot click out of.
   *
   * @param {object} [options]
   * @param {boolean} [options.mirror] - Follow the product page's size, unless the shopper set their own.
   */
  #syncOptions({ mirror = false } = {}) {
    const selects = this.#selects;

    // Products with a single default variant have no selects to read.
    if (!selects.length) {
      this.#selectedVariant = this.#variants[0] ?? null;
      return;
    }

    const sizeIndex = this.#sizeOptionIndex;
    const allowed = this.#allowedSizeValues();

    const lists = selects.map((select, index) => {
      const values = this.#optionValues[index] ?? Array.from(select.options, (option) => option.value);
      if (index !== sizeIndex || !allowed) return values;

      const narrowed = values.filter((value) => allowed.has(normalise(value)));

      return narrowed.length ? narrowed : values;
    });

    const chosen = selects.map((select, index) => {
      const values = lists[index] ?? [];
      const keepsCurrent = values.includes(select.value);

      if (index === sizeIndex) {
        const followsProductPage = mirror && !this.#sizeChosenByShopper;

        return keepsCurrent && !followsProductPage ? select.value : this.#pickSizeValue(index, values, select.value);
      }

      return keepsCurrent ? select.value : values[0] ?? select.value;
    });

    selects.forEach((select, index) => {
      this.#paintSelect(select, index, lists[index] ?? [], chosen);
      select.value = chosen[index] ?? select.value;
    });

    this.#selectedVariant = this.#findVariant(chosen) ?? null;
  }

  /**
   * Rewrites one select to offer exactly `values`, annotating what cannot be bought alongside the
   * rest of the current selection. The element itself is never replaced, so refs stay valid.
   * @param {HTMLSelectElement} select
   * @param {number} index
   * @param {string[]} values
   * @param {string[]} combination - The option values the whole picker is settling on.
   */
  #paintSelect(select, index, values, combination) {
    const unavailableSuffix = this.dataset.unavailableSuffix ?? '';
    const existing = Array.from(select.options, (option) => option.value);
    const unchanged = existing.length === values.length && existing.every((value, at) => value === values[at]);

    if (!unchanged) {
      select.replaceChildren(
        ...values.map((value) => {
          const option = document.createElement('option');
          option.value = value;
          return option;
        })
      );
    }

    for (const option of select.options) {
      const probe = [...combination];
      probe[index] = option.value;

      const available = Boolean(this.#findVariant(probe)?.available);
      const label = available || !unavailableSuffix ? option.value : `${option.value} - ${unavailableSuffix}`;

      if (option.textContent !== label) option.textContent = label;
    }
  }

  /* -------------------------------------------------------------------------------------------
   * Rendering
   * ---------------------------------------------------------------------------------------- */

  /**
   * @param {number} cents
   * @returns {string}
   */
  #money(cents) {
    return formatMoney(cents, this.#moneyFormat, this.dataset.currency ?? '');
  }

  /**
   * Writes a price into the `[data-amount]` slot of a price element, leaving the visually hidden
   * "Regular price" labels that screen readers rely on untouched.
   * @param {Element | undefined} element
   * @param {number} cents
   */
  #setPrice(element, cents) {
    const target = element?.querySelector('[data-amount]') ?? element;
    if (target) target.textContent = this.#money(cents);
  }

  /**
   * Mirrors the block's Liquid pricing so the client-side numbers can never drift from the
   * server-rendered ones.
   */
  #prices() {
    const crossPrice = this.#selectedVariant?.price ?? 0;
    const regularTotal = this.#mainPrice + crossPrice;
    const fixedPrice = this.#fixedBundlePrice;

    if (fixedPrice > 0) {
      // A fixed price is a promise about the pair, so it can never exceed what the two items cost
      // on their own - a "discount" that raised the total would be worse than showing none.
      const bundleTotal = Math.min(fixedPrice, regularTotal);

      return {
        crossPrice,
        regularTotal,
        discountAmount: regularTotal - bundleTotal,
        bundleTotal,
        // A fixed price covers the pair, so none of it can be attributed to a single line.
        itemDiscounted: false,
      };
    }

    const base = this.dataset.discountAppliesTo === 'bundle_total' ? regularTotal : crossPrice;
    const discountAmount = Math.round((base * this.#discountPercentage) / 100);

    return {
      crossPrice,
      regularTotal,
      discountAmount,
      bundleTotal: regularTotal - discountAmount,
      itemDiscounted: this.dataset.discountAppliesTo !== 'bundle_total' && discountAmount > 0,
    };
  }

  #render() {
    const { itemPriceWas, itemPriceNow, regularTotal, bundleTotal, savingsLine, savingsBadge, crossImage } = this.refs;
    const prices = this.#prices();
    const hasSavings = prices.discountAmount > 0;

    if (itemPriceNow) {
      const effectiveItemPrice = prices.itemDiscounted ? prices.crossPrice - prices.discountAmount : prices.crossPrice;

      this.#setPrice(itemPriceNow, effectiveItemPrice);
      itemPriceNow.classList.toggle('bro-cross-sell__item-price-now--discounted', prices.itemDiscounted);
    }

    if (itemPriceWas) {
      this.#setPrice(itemPriceWas, prices.crossPrice);
      itemPriceWas.classList.toggle('hidden', !prices.itemDiscounted);
    }

    if (regularTotal) {
      this.#setPrice(regularTotal, prices.regularTotal);
      regularTotal.classList.toggle('hidden', !hasSavings);
    }

    this.#setPrice(bundleTotal, prices.bundleTotal);

    if (savingsLine) {
      const template = savingsLine.dataset.savingsTemplate ?? '';
      savingsLine.textContent = template.replace('%%amount%%', this.#money(prices.discountAmount));
      savingsLine.classList.toggle('hidden', !hasSavings);
    }

    if (savingsBadge) {
      // The badge states the saving in money: with a fixed bundle price the cash figure is the
      // number the merchant actually set, and it survives variants of differing price.
      const template = savingsBadge.dataset.badgeTemplate ?? '';

      savingsBadge.textContent = template.replace('%%amount%%', this.#money(prices.discountAmount));
      savingsBadge.classList.toggle('hidden', !hasSavings);
    }

    const variantImage = this.#selectedVariant?.image;
    if (crossImage && variantImage && crossImage.src !== variantImage) {
      crossImage.src = variantImage;
      crossImage.srcset = this.#selectedVariant?.image2x
        ? `${variantImage} 1x, ${this.#selectedVariant.image2x} 2x`
        : variantImage;
    }

    this.#renderButton();
  }

  #renderButton() {
    if (this.#isSubmitting) return;

    const { addButton, addButtonText } = this.refs;
    const canAdd = this.#mainAvailable && Boolean(this.#selectedVariant?.available);

    addButton.disabled = !canAdd;
    addButtonText.textContent = canAdd ? this.dataset.addText ?? '' : this.dataset.soldOutText ?? '';
  }

  /* -------------------------------------------------------------------------------------------
   * Event handlers
   * ---------------------------------------------------------------------------------------- */

  /**
   * Quick variant selection: resolve and repaint entirely client side, with no network request.
   * @param {Event} [event]
   */
  handleOptionChange = (event) => {
    const target = event?.target;

    if (target instanceof HTMLSelectElement && this.#selects.indexOf(target) === this.#sizeOptionIndex) {
      this.#sizeChosenByShopper = true;
    }

    this.#syncOptions();
    this.#clearError();
    this.#render();
  };

  /**
   * Keeps the bundle in step with the variant picker of the product being viewed.
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleMainProductSelect = (event) => {
    // Variant changes inside a product card belong to that card, not to the main product.
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Read synchronously: the payload belongs to the event, not to the section that follows it.
    const sizeOptionName = this.dataset.sizeOptionName;
    const selectedOptions = Array.isArray(event.selectedOptions) ? event.selectedOptions : [];
    const hostSize = sizeOptionName
      ? selectedOptions.find((option) => normalise(option?.name) === normalise(sizeOptionName))?.value
      : undefined;

    event.promise
      .then(({ detail }) => {
        if (!detail) return;

        const { resource, newProduct, productId } = detail;

        if (newProduct) {
          // Combined listing: the picker swapped in a different product.
          this.dataset.mainProductId = newProduct.id;
        } else if (productId && productId !== this.dataset.mainProductId) {
          return;
        }

        if (resource == null) {
          this.dataset.mainAvailable = 'false';
        } else {
          this.dataset.mainVariantId = String(resource.id ?? '');
          this.dataset.mainPrice = String(resource.price ?? 0);
          this.dataset.mainAvailable = String(resource.available !== false);

          const mainImageSrc = resource.featured_media?.preview_image?.src;
          const { mainImage } = this.refs;
          if (mainImage && mainImageSrc) {
            const src = `${mainImageSrc}${mainImageSrc.includes('?') ? '&' : '?'}width=96`;
            mainImage.src = src;
            mainImage.srcset = src;
          }
        }

        // A move between the kids and adult ranges has to reach the companion's dropdown, even when
        // the combination the shopper landed on has no variant of its own.
        const sizeChanged = hostSize != null && hostSize !== this.dataset.mainSizeValue;
        if (sizeChanged) this.dataset.mainSizeValue = hostSize;

        this.#syncOptions({ mirror: sizeChanged });
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bro-cross-sell-bundle] Event promise rejected:', error);
      });
  };

  /**
   * Quick add: both lines go into the cart in a single request, so the shopper never leaves the
   * product page and the bundle can never end up half-added.
   * @param {Event} event
   */
  handleAdd = async (event) => {
    if (this.#isSubmitting) return;

    const mainVariantId = this.dataset.mainVariantId;
    const crossVariant = this.#selectedVariant;

    if (!mainVariantId || !crossVariant?.available || !this.#mainAvailable) {
      this.#renderButton();
      return;
    }

    const lines = [
      { merchandiseId: String(mainVariantId), quantity: 1 },
      { merchandiseId: String(crossVariant.id), quantity: 1 },
    ];

    this.#setSubmitting(true);
    this.#clearError();

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferredEventPromise.promise,
      })
    );

    const sectionIds = this.#cartSectionIds();
    const discountCode = this.dataset.discountCode?.trim();

    try {
      const addResponse = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', {
          body: JSON.stringify({
            items: lines.map((line) => ({ id: Number(line.merchandiseId), quantity: line.quantity })),
            // When a discount follows, the cart sections are rendered by that later request instead,
            // so the drawer never paints a total that predates the discount.
            ...(discountCode || !sectionIds ? {} : { sections: sectionIds }),
          }),
        })
      );

      const addData = await addResponse.json();

      if (addData.status) {
        this.#handleAddError(addData, deferredEventPromise);
        return;
      }

      const { cart, sections } = discountCode
        ? await this.#applyDiscountCode(discountCode, sectionIds)
        : { cart: await this.#fetchCart(), sections: addData.sections };

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bro-cross-sell-bundle',
          sourceId: this.id.toString(),
          itemCount: lines.length,
          productId: this.dataset.mainProductId,
          sections,
          didError: false,
        },
      });

      this.#announce(this.dataset.addedText ?? '');
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      this.#showError(this.dataset.errorText ?? '');
    } finally {
      this.#setSubmitting(false);
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  };

  /* -------------------------------------------------------------------------------------------
   * Cart helpers
   * ---------------------------------------------------------------------------------------- */

  /** @returns {string} Comma-separated section ids to re-render, or an empty string. */
  #cartSectionIds() {
    const ids = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) ids.push(element.dataset.sectionId);
    }
    return ids.join(',');
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }

  /**
   * Applies the merchant's bundle discount code on top of any codes already on the cart, so an
   * existing promotion is never silently dropped.
   * @param {string} discountCode
   * @param {string} sectionIds
   * @returns {Promise<{cart: any, sections: any}>}
   */
  async #applyDiscountCode(discountCode, sectionIds) {
    const cart = await this.#fetchCart();
    const existingCodes = Array.isArray(cart.discount_codes)
      ? cart.discount_codes.map((/** @type {{code: string}} */ discount) => discount.code).filter(Boolean)
      : [];

    const codes = existingCodes.includes(discountCode) ? existingCodes : [...existingCodes, discountCode];

    const response = await fetch(
      Theme.routes.cart_update_url,
      fetchConfig('json', {
        body: JSON.stringify({
          discount: codes.join(','),
          ...(sectionIds ? { sections: sectionIds } : {}),
        }),
      })
    );

    if (!response.ok) return { cart, sections: undefined };

    const updatedCart = await response.json();

    const rejected = updatedCart.discount_codes?.find(
      (/** @type {{code: string, applicable: boolean}} */ discount) =>
        discount.code === discountCode && discount.applicable === false
    );

    if (rejected) {
      // The shopper still gets both products; only the merchant needs to know the code is wrong.
      console.warn(
        `[bro-cross-sell-bundle] Discount code "${discountCode}" was not applicable to the cart. Check that a matching discount exists and its conditions are met.`
      );
    }

    return { cart: updatedCart, sections: updatedCart.sections };
  }

  /**
   * @param {{message?: string, description?: string, errors?: unknown}} data
   * @param {ReturnType<typeof CartLinesUpdateEvent.createPromise>} deferredEventPromise
   */
  #handleAddError(data, deferredEventPromise) {
    this.dispatchEvent(
      new CartErrorEvent({
        error: data.message || 'Add to cart failed',
        code: 'INVALID',
        detail: { description: data.description, errors: data.errors },
      })
    );

    this.#showError(data.description || data.message || this.dataset.errorText || '');

    this.#fetchCart()
      .then((cart) =>
        deferredEventPromise.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'bro-cross-sell-bundle',
            sourceId: this.id.toString(),
            productId: this.dataset.mainProductId,
          },
        })
      )
      .catch(deferredEventPromise.reject);
  }

  /* -------------------------------------------------------------------------------------------
   * UI state
   * ---------------------------------------------------------------------------------------- */

  /** @param {boolean} isSubmitting */
  #setSubmitting(isSubmitting) {
    const { addButton } = this.refs;

    this.#isSubmitting = isSubmitting;
    addButton.setAttribute('aria-busy', String(isSubmitting));

    if (isSubmitting) {
      addButton.disabled = true;
    } else {
      this.#renderButton();
    }
  }

  /** @param {string} message */
  #showError(message) {
    if (!message) return;

    const { errorMessage } = this.refs;

    errorMessage.textContent = message;
    errorMessage.classList.remove('hidden');
    this.#announce(message);

    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#clearError(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #clearError() {
    const { errorMessage } = this.refs;

    clearTimeout(this.#errorTimeout);
    errorMessage.textContent = '';
    errorMessage.classList.add('hidden');
  }

  /** @param {string} message */
  #announce(message) {
    if (!message) return;

    const { liveRegion } = this.refs;

    liveRegion.textContent = message;

    clearTimeout(this.#successTimeout);
    this.#successTimeout = setTimeout(() => {
      liveRegion.textContent = '';
    }, SUCCESS_MESSAGE_DISPLAY_DURATION);
  }
}

/**
 * Case- and whitespace-insensitive form of an option value, so "140 " and "140" are one size and
 * "Größe" matches "größe" across the two products.
 * @param {string | null | undefined} value
 * @returns {string}
 */
function normalise(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * Resolves HTML entities that survive a `| escape`d Liquid attribute (money formats commonly carry
 * things like `&euro;`), so the value is safe to write with textContent. Setting innerHTML on a
 * detached textarea parses RCDATA only - markup is never executed.
 * @param {string} value
 * @returns {string}
 */
function decodeEntities(value) {
  if (!value.includes('&')) return value;

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;

  return textarea.value;
}

if (!customElements.get('bro-cross-sell-bundle')) {
  customElements.define('bro-cross-sell-bundle', BroCrossSellBundle);
}
